import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wand2, Lock, Save, Check, Gift, Sparkles } from "lucide-react";
import api from "../lib/api";

const ReelBrandingCard = ({ event, onUpdate }) => {
  const paid = !!event?.paid;
  const referralUnlocked = !!event?.reel_branding_unlocked_by_referral;
  const canCustomize = paid || referralUnlocked;

  const [title, setTitle] = useState(event?.reel_title || "");
  const [subtitle, setSubtitle] = useState(event?.reel_subtitle || "");
  const [hideBrand, setHideBrand] = useState(!!event?.reel_hide_branding);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  // Fetch referral rewards so we know whether to show the "Unlock with credit" CTA
  const [reelAvailable, setReelAvailable] = useState(0);
  const [unlocking, setUnlocking] = useState(false);
  useEffect(() => {
    if (canCustomize) return;
    api.get("/referrals/me")
      .then((r) => setReelAvailable(r.data?.rewards?.reel_available || 0))
      .catch(() => {});
  }, [canCustomize]);

  const save = async () => {
    setBusy(true); setErr(""); setSaved(false);
    try {
      const res = await api.patch(`/events/${event.id}`, {
        reel_title: title,
        reel_subtitle: subtitle,
        reel_hide_branding: hideBrand,
      });
      onUpdate(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save reel branding.");
    } finally {
      setBusy(false);
    }
  };

  const unlockWithReferral = async () => {
    if (!window.confirm("Use one of your referral reel-unlock credits on this event? Custom title, subtitle, and hidden branding will be enabled for this event only.")) return;
    setUnlocking(true); setErr("");
    try {
      const res = await api.post(`/events/${event.id}/reel/unlock-referral`);
      onUpdate(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not unlock with referral credit.");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl soft-shadow p-6" data-testid="reel-branding-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-coral-300" />
          <h3 className="font-serif italic text-navy-700 text-xl">Reel branding</h3>
        </div>
        {!paid && !referralUnlocked && (
          <span className="text-[10px] uppercase tracking-wider font-bold bg-coral-300/15 text-coral-500 px-2 py-1 rounded-full inline-flex items-center gap-1">
            <Lock className="w-3 h-3" /> Paid tier
          </span>
        )}
        {referralUnlocked && !paid && (
          <span data-testid="reel-branding-referral-unlocked-badge" className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Unlocked via referral
          </span>
        )}
      </div>
      <p className="text-sm text-navy-700/60 mb-5">
        Customize how your highlights reel looks. Override the cover title, add a subtitle,
        and remove the &ldquo;Powered by GalerieCop&rdquo; footer for a fully branded share page.
      </p>

      {!canCustomize ? (
        <div className="space-y-3" data-testid="reel-branding-upsell">
          {reelAvailable > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start justify-between gap-4" data-testid="reel-branding-referral-unlock-panel">
              <div className="text-sm text-emerald-900">
                <div className="font-semibold inline-flex items-center gap-1.5">
                  <Gift className="w-4 h-4" /> You have {reelAvailable} referral reel-unlock credit{reelAvailable === 1 ? "" : "s"}
                </div>
                <p className="mt-1 text-emerald-800/80">
                  Redeem one now to unlock custom branding on this event — no tier upgrade needed.
                </p>
              </div>
              <button
                onClick={unlockWithReferral}
                disabled={unlocking}
                data-testid="reel-branding-referral-unlock-btn"
                className="shrink-0 bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                {unlocking ? "Unlocking…" : "Unlock free"}
              </button>
            </div>
          )}
          <div className="bg-cream-100 rounded-xl p-4 flex items-start justify-between gap-4">
            <div className="text-sm text-navy-700/70">
              Reel branding customization is included in <b>Petite</b>, <b>Classic</b>, and <b>Grand</b> tiers.
              Or earn a free unlock every 3 referrals — share your invite link from the dashboard.
            </div>
            <Link
              to={`/dashboard/events/${event.id}/upgrade`}
              data-testid="reel-branding-upgrade-cta"
              className="shrink-0 bg-coral-300 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-coral-400"
            >
              Upgrade
            </Link>
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Cover title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={event.name}
              maxLength={80}
              data-testid="reel-title-input"
              className="mt-2 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-navy-700"
            />
            <p className="text-xs text-navy-700/50 mt-1">Leave blank to use the event name.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Subtitle</label>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="e.g. Sarah &amp; Michael — March 2026"
              maxLength={120}
              data-testid="reel-subtitle-input"
              className="mt-2 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-navy-700"
            />
            <p className="text-xs text-navy-700/50 mt-1">Leave blank to use the event date.</p>
          </div>
          <label className="flex items-center justify-between gap-3 py-3 border-t border-cream-200">
            <div>
              <span className="text-navy-700 font-medium">Hide &ldquo;Powered by GalerieCop&rdquo; footer</span>
              <p className="text-xs text-navy-700/50 mt-0.5">Only visible on the shareable reel page.</p>
            </div>
            <input
              type="checkbox"
              checked={hideBrand}
              onChange={(e) => setHideBrand(e.target.checked)}
              data-testid="reel-hide-branding-toggle"
              className="w-5 h-5 accent-coral-300"
            />
          </label>
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={save}
              disabled={busy}
              data-testid="reel-branding-save-btn"
              className="inline-flex items-center gap-2 bg-navy-700 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-navy-800 disabled:opacity-60"
            >
              {saved ? <><Check className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save branding"}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelBrandingCard;
